/**
 * @file: ChatTaskCardCompact.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * A compact TaskCardV2 used as the body of an order / service-record chat
 * card. It reuses the real task-card sub-components + utils. It renders the
 * payload SNAPSHOT first, then revalidates from live task detail via
 * `useLiveTaskCard` (stale-while-revalidate). No report-progress footer —
 * that needs more live state than a chat bubble warrants. Mirrors the full
 * task card's header → info grid → status, trimmed for a chat bubble.
 */

import { DashedSeparator } from '@euler/app/components/task-card/components/DashedSeparator';
import { FuelTypeChip } from '@euler/app/components/task-card/components/FuelTypeChip';
import { InfoCell } from '@euler/app/components/task-card/components/InfoCell';
import { ModelBadge } from '@euler/app/components/task-card/components/ModelBadge';
import { StatusBadgeV2 } from '@euler/app/components/task-card/components/StatusBadgeV2';
import { kLogoSize, TOKENS } from '@euler/app/components/task-card/constants';
import {
  computeInShopDuration,
  computeVehicleAge,
  formatMileage,
} from '@euler/app/components/task-card/utils';
import { Typography } from '@euler/app/components/typography';
import { taskCardBaseFromTaskDetail } from '@euler/app/flows/chat/utils/chatCardDescriptor';
import { Avatar } from '@euler/components/Avatar';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ChatMessagePayloadOrderCard,
  ChatMessagePayloadServiceRecordCard,
  ChatTaskCardBase,
} from '@euler/model/chat';
import { useServiceFactory } from '@euler/services/factory';
import { formatDateTime } from '@euler/utils/formatters';
import { Gauge } from 'lucide-react-native';
import { FC, useEffect, useState } from 'react';
import { View } from 'react-native';
import { ChatCardShell } from './ChatCardShell';

type Payload =
  | ChatMessagePayloadOrderCard
  | ChatMessagePayloadServiceRecordCard;

export const ChatTaskCardCompact: FC<{
  payload: Payload;
  onPress?: () => void;
}> = ({ payload, onPress }) => {
  const { rpx } = useResponsiveContext();
  // Render the snapshot immediately, then revalidate from live task detail.
  const card = useLiveTaskCard(payload);
  const v = card.vehicle;
  const age = computeVehicleAge(v?.modelYear);
  const inShop = computeInShopDuration(card.orderedAt, card.finishedAt);
  const buildTs = card.finishedAt ?? card.orderedAt;
  const fuel =
    v?.fuelType?.trim() && v.fuelType.trim() !== '未知'
      ? v.fuelType.trim()
      : '汽油';

  return (
    <ChatCardShell
      onPress={onPress}
      accent={card.status?.tone}
      width={rpx(280)}
    >
      {/* HEADER */}
      <View
        css={`
          padding: ${rpx(12)}px ${rpx(14)}px ${rpx(2)}px;
        `}
      >
        <HStack justifyContent="flex-start" alignItems="center">
          {v?.brandLogo ? (
            <Img
              uri={v.brandLogo}
              css={`
                width: ${rpx(kLogoSize)}px;
                height: ${rpx(kLogoSize)}px;
                flex-shrink: 0;
              `}
            />
          ) : (
            <Avatar
              name={v?.name ?? v?.licensePlateNo ?? '车'}
              css={`
                width: ${rpx(kLogoSize)}px;
                height: ${rpx(kLogoSize)}px;
                border-radius: ${rpx(kLogoSize)}px;
                flex-shrink: 0;
              `}
              textStyle={{ fontSize: rpx(11) }}
            />
          )}
          <Typography.Label
            bold
            size={rpx(15)}
            lineHeight={rpx(20)}
            numberOfLines={1}
            color={TOKENS.textPrimary}
            css={`
              margin-left: ${rpx(8)}px;
            `}
          >
            {v?.licensePlateNo ?? card.orderNo}
          </Typography.Label>
          {v?.model && v.model !== '未知' ? (
            <ModelBadge label={v.model} />
          ) : null}
        </HStack>

        <HStack
          justifyContent="flex-start"
          alignItems="center"
          css={`
            margin-top: ${rpx(6)}px;
            padding-left: ${rpx(kLogoSize + 8)}px;
          `}
        >
          <FuelTypeChip fuelType={fuel} />
          <Typography.Label
            size={rpx(10)}
            lineHeight={rpx(14)}
            color={TOKENS.textMuted}
            css={`
              margin-left: ${rpx(8)}px;
            `}
          >
            车龄{' '}
            <Typography.Text medium size={rpx(10)} color={TOKENS.textPrimary}>
              {age != null ? `${age}年` : '-'}
            </Typography.Text>
          </Typography.Label>
          {v?.brandName ? (
            <Typography.Label
              size={rpx(10)}
              lineHeight={rpx(14)}
              color={TOKENS.textMuted}
              numberOfLines={1}
              css={`
                margin-left: ${rpx(6)}px;
              `}
            >
              · {v.brandName}
            </Typography.Label>
          ) : null}
        </HStack>
      </View>

      <DashedSeparator />

      {/* INFO GRID */}
      <View
        css={`
          padding: ${rpx(10)}px ${rpx(14)}px;
          flex-direction: row;
          flex-wrap: wrap;
        `}
      >
        <InfoCell
          size={11}
          iconNode={
            <Gauge size={rpx(11)} color={TOKENS.textFaint} strokeWidth={2} />
          }
          label="里程"
          value={v?.mileage != null ? formatMileage(v.mileage) : '-'}
        />
        <InfoCell
          size={11}
          icon="clock"
          label={card.finishedAt ? '耗时' : '在厂'}
          value={inShop}
        />
        <InfoCell
          size={11}
          icon="user"
          label="顾问"
          value={card.serviceAgentName}
        />
        <InfoCell
          size={11}
          icon="clipboard"
          label={card.finishedAt ? '完工' : '建单'}
          value={buildTs ? formatDateTime(buildTs, 'M/d HH:mm') : '-'}
        />
      </View>

      <DashedSeparator />

      {/* STATUS */}
      <HStack
        justifyContent="flex-start"
        alignItems="center"
        css={`
          padding: ${rpx(10)}px ${rpx(14)}px ${rpx(12)}px;
        `}
      >
        {card.status ? (
          <StatusBadgeV2 label={card.status.label} palette={card.status.tone} />
        ) : null}
      </HStack>
    </ChatCardShell>
  );
};

/**
 * Stale-while-revalidate for the card: return the payload SNAPSHOT immediately
 * (instant render), then fetch live task detail by `taskNo` and return a
 * refreshed base merged over the snapshot — so a shared card reflects
 * status/mileage/dates that moved on after it was sent.
 *
 * Only revalidates when a `taskNo` is present. In-progress order cards carry
 * one; finished orders / service-record cards don't (they deep-link by
 * serviceRecordId and their state is terminal), so they keep the snapshot.
 */
function useLiveTaskCard<T extends { taskNo?: string }>(payload: T): T {
  const { taskService } = useServiceFactory();
  const taskNo = payload.taskNo;
  const [live, setLive] = useState<ChatTaskCardBase | null>(null);

  useEffect(() => {
    setLive(null);
    if (!taskNo) return;
    let cancelled = false;
    taskService
      .getTaskDetail(taskNo, {
        projection: {
          basicInfo: true,
          preInspection: true,
          inspection: true,
          construction: true,
          deliveryCheck: true,
        },
      })
      .then(res => {
        if (!cancelled) setLive(taskCardBaseFromTaskDetail(res.response));
      })
      .catch(err => {
        // Best-effort refresh; keep the snapshot on failure.
        console.error('chat task card revalidate failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [taskNo, taskService]);

  return live ? ({ ...payload, ...live } as T) : payload;
}
